import { useRef, useState, type ReactNode } from 'react';
import { Caption1, Text, makeStyles } from '@fluentui/react-components';
import { ShellIcons, StatusDot, complianceTokenFor, shellMedia, shellMetrics, tokens, type ComplianceTone } from '@vigeo/ui';
import { offerAcceptByText, type OfferRecord, type Person } from '@vigeo/shared';
import { formatRecordedCalendarDate } from '@vigeo/shared';
import { rateText } from '@/features/offers';
import { OfferTracker } from '../OfferTracker.js';
import { offerTrackerSteps } from '../model.js';
import { OfferPreviewDialog } from './OfferPreviewDialog.js';
import { PortalPreview } from './PortalPreview.js';

const useStyles = makeStyles({
  card: {
    display: 'flex', flexDirection: 'column', minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge, overflow: 'hidden',
  },
  head: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL, padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  headText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, flexGrow: 1, flexBasis: '20rem', minWidth: 0 },
  title: { margin: 0, fontSize: tokens.fontSizeBase500, lineHeight: tokens.lineHeightBase500, fontWeight: tokens.fontWeightSemibold },
  lede: { color: tokens.colorNeutralForeground2 },
  actions: { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalM },
  body: {
    display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', gap: tokens.spacingHorizontalXXL,
    padding: tokens.spacingHorizontalXL, minWidth: 0,
  },
  label: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, width: '12.5rem', flexShrink: 0, minWidth: 0 },
  labelName: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
  fields: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, flexGrow: 1, flexBasis: '20rem', minWidth: 0 },
  rows: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, margin: 0 },
  rowItem: { display: 'flex', flexDirection: 'row', gap: tokens.spacingHorizontalL, minWidth: 0 },
  rowKey: { width: '10rem', flexShrink: 0, color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, margin: 0 },
  rowValue: { margin: 0, minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-line', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300 },
  pdf: {
    display: 'flex', flexDirection: 'row', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', minWidth: 0,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium, border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  pdfIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: tokens.spacingHorizontalXXXL, height: tokens.spacingHorizontalXXXL, borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground1,
  },
  pdfText: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, flexGrow: 1, flexBasis: '12rem', minWidth: 0 },
  pdfName: { fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  view: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXXS, color: tokens.colorBrandForeground1,
    textDecorationLine: 'none', fontSize: tokens.fontSizeBase300, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold,
    background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', cursor: 'pointer',
    [shellMedia.phone]: { minHeight: shellMetrics.touchTarget },
  },
  stage: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalM, paddingInline: tokens.spacingHorizontalXL,
    borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, backgroundColor: tokens.colorNeutralBackground2,
  },
});

const toneColor: Record<ComplianceTone | 'neutral', string> = {
  good: complianceTokenFor.good, warning: complianceTokenFor.warning, critical: complianceTokenFor.critical,
  attention: complianceTokenFor.attention, neutral: tokens.colorNeutralForeground3,
};

export interface OfferSummaryProps {
  readonly person: Person;
  readonly record: OfferRecord;
  readonly badge: { readonly label: string; readonly tone: ComplianceTone | 'neutral' };
  readonly title: string;
  readonly lede: string;
  /** The tracker under the lede; absent where the frame draws none. */
  readonly reached?: number;
  readonly actions: ReactNode;
  /** The label column's heading ("Offer", "Expired offer") and the line under it. */
  readonly offerLabel: string;
  readonly offerHint: string;
  /** Rows above Position — the reason, on a sent-back offer. */
  readonly lead?: readonly { readonly label: string; readonly value: string }[];
}

/**
 * The one card the #439 offer frames share (970:13378, 970:13595, 971:13847, 971:14266): a status
 * line, the headline with its single primary action at the right, an optional tracker, the offer's
 * facts, the letter, and the stage line.
 *
 * Everything on it is read from the offer record; nothing is stored for it.
 */
export function OfferSummary({ person, record, badge, title, lede, reached, actions, offerLabel, offerHint, lead = [] }: OfferSummaryProps) {
  const styles = useStyles();
  const [viewing, setViewing] = useState(false);
  const viewOpener = useRef<HTMLElement | null>(null);
  const { terms } = record;
  const rows = [
    ...lead,
    { label: 'Position', value: terms.position },
    { label: 'Classification', value: `${terms.employmentClassification === 'w2' ? 'W-2' : '1099'} · ${terms.employmentType}` },
    { label: 'Proposed start', value: formatRecordedCalendarDate(terms.proposedStartDate) },
    { label: 'Rates', value: terms.compensation.map(rateText).join(' · ') },
    { label: 'Accept by', value: offerAcceptByText(terms.acceptanceDeadline) },
  ];
  return (
    <section className={styles.card} aria-label={title}>
      <div className={styles.head}>
        <div className={styles.headText}>
          <div><StatusDot color={toneColor[badge.tone]}>{badge.label}</StatusDot></div>
          <h3 className={styles.title}>{title}</h3>
          <Text className={styles.lede}>{lede}</Text>
          {reached === undefined ? null : <OfferTracker steps={offerTrackerSteps} reached={reached} label="Offer status" />}
        </div>
        <div className={styles.actions}>{actions}</div>
      </div>
      <div className={styles.body}>
        <div className={styles.label}>
          <span className={styles.labelName}>{offerLabel}</span>
          <Caption1>{offerHint}</Caption1>
        </div>
        <div className={styles.fields}>
          <dl className={styles.rows}>
            {rows.map((row) => (
              <div key={row.label} className={styles.rowItem}>
                <dt className={styles.rowKey}>{row.label}</dt>
                <dd className={styles.rowValue}>{row.value}</dd>
              </div>
            ))}
          </dl>
          <div className={styles.pdf}>
            <span className={styles.pdfIcon} aria-hidden="true"><ShellIcons.document /></span>
            <span className={styles.pdfText}>
              <span className={styles.pdfName}>Offer letter – {record.candidateName}.pdf</span>
              <Caption1>PDF · prepared from the offer terms</Caption1>
            </span>
            {/* A pop-up, as on 1.4: the review page says "Nothing is sent yet", which is wrong for an offer that has gone out. */}
            <button type="button" className={styles.view} onClick={(event) => { viewOpener.current = event.currentTarget; setViewing(true); }}>
              View<ShellIcons.externalLink />
            </button>
          </div>
        </div>
      </div>
      <div className={styles.stage}>
        <Text weight="semibold">Stage</Text>
        <StatusDot color={toneColor.neutral}>Candidate</StatusDot>
        <Text size={200}>Moves to Onboarding after a signed offer.</Text>
      </div>
      <OfferPreviewDialog open={viewing} opener={viewOpener} title={`Offer letter – ${record.candidateName}`} onClose={() => setViewing(false)}>
        {viewing ? <PortalPreview person={person} record={record} letterOnly /> : null}
      </OfferPreviewDialog>
    </section>
  );
}
